import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ColorPallet } from "../utils/colorPallet";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { getSalesHistoryData } from "../features/salesHistory/salesHistorySlice";
import { SalesHistoryModal } from "../components/salesHistory/salesHistoryModal";
import AppModal from "../components/common/AppModal"; // Import AppModal component
import SalesHistoryTable from "../features/salesHistory/salesHistoryTable";
import AppButton from "../components/common/AppButton";
import LoadingSpinner from "../components/common/Loading";
import PdfIcon from "../components/IconComp/pdfIcon";
import { ReturnInvoiceModal } from "../components/return-item/return-invoice-model";
import { InvoiceModal } from "../components/sales/invoice-model";
import { getAllStaffPermissions } from "../features/staff/staffSlice";
import SalesHistoryPdf from "../components/salesHistory/salesHistoryPdf";
import SearchIcon from "../components/IconComp/searchIcon";
import PdfDownloadIcon from "../components/IconComp/pdfDownloadIcon";

const headerData = [
  "Invoice Number",
  "Date",
  "Total Amount(RS.)",
  "Discount(%)",
  "Discount Amount",
  "Total Payable",
  "Paid",
  "Due(s)",
  "Change Returned",
  "Action",
];
const getCurrentDate = () => {
  const today = new Date();
  const year = today?.getFullYear();
  const month = String(today?.getMonth() + 1)?.padStart(2, "0");
  const day = String(today?.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const SalesHistory = () => {
  const dispatch = useAppDispatch();
  const [dateFilterType, setDateFilterType] = useState("single");
  const [searchQuery, setSearchQuery] = useState("");
  // const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(getCurrentDate()); // Set the current date by default
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const [selectedRow, setSelectedRow] = useState<any>(null); // State to store selected row
  const [isAppModalOpen, setIsAppModalOpen] = useState<boolean>(false); // State to manage app modal visibility
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [totalDiscount, setTotalDiscount] = useState<number>(0);
  const [totalProfit, setTotalProfit] = useState<number>(0);
  const [totalCost, setTotalCost] = useState<number>(0);
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
  const [invoiceId, setInvoiceId] = useState<any>();
  const [sale, setSale] = useState<any>([]);
  const [isReturnModal, setIsReturnModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | string>("");
  //pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const Account_Type = parseInt(
    localStorage.getItem("pharmacy_account_type") || "0",
    10
  );
  const { staffPermissionsPayload }: any = useAppSelector(
    (state) => state?.staff
  );
  const pharmacy_Id: any = localStorage?.getItem("pharmacyId");

  const handleChange = (event: any) => {
    setSelectedId(event?.target?.value);
    setPage(0);
  };

  const GetAllStaffPermissions = useCallback(() => {
    dispatch(getAllStaffPermissions(pharmacy_Id)).then((res: any) => {
      if (res?.payload?.success === true) {
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (pharmacy_Id) {
      GetAllStaffPermissions();
    }
  }, [pharmacy_Id, GetAllStaffPermissions]);

  const options = staffPermissionsPayload
    ? staffPermissionsPayload?.map((item: any) => ({
        id: item?.staff?.id,
        name:
          item?.staff?.name.charAt(0).toUpperCase() +
          item?.staff?.name.slice(1).toLowerCase(),
      }))
    : [];

  const handleOpenInvoiceModal = (
    invoiceNumber: any,
    sales: any,
    isReturn: any
  ) => {
    setInvoiceId(invoiceNumber);
    setSale(sales);
    setIsReturnModal(isReturn);
    setOpenInvoiceModal(true);
  };
  const handleCloseInvoiceModal = () => {
    setOpenInvoiceModal(false);
  };

  const rowData: { salesHistoryDetails: any } = useAppSelector(
    (state) => state?.salesHistory
  );

  const pharmacyId = localStorage?.getItem("pharmacyId");

  const getSalesHistory = useCallback(async () => {
    const dto: any = {
      page: page + 1, // API pages are 1-indexed
      limit: rowsPerPage,
      start_date:
        dateFilterType === "single"
          ? selectedDate
          : fromDate
          ? fromDate
          : undefined,
      end_date: toDate ? toDate : undefined, // Assuming there's a selected date
      id: selectedId ? selectedId : undefined, // Only send if it's a number or string
      search: searchQuery ? searchQuery : undefined,
    };

    try {
      setIsLoading(true);
      const res = await dispatch(
        getSalesHistoryData({ pharmacy_id: pharmacyId, dto })
      );
      if (res.payload) {
        setTotalCount(res?.payload?.pagination?.totalItems || 0); // Ensure number type
      }
    } catch (error) {
      console.error("Error fetching manufacturer data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    dispatch,
    pharmacyId,
    page,
    rowsPerPage,
    selectedDate,
    toDate,
    fromDate,
    selectedId,
    searchQuery,
  ]);

  useEffect(() => {
    getSalesHistory();
  }, [getSalesHistory]);

  useEffect(() => {
    setSelectedDate(getCurrentDate()); // Update selectedDate whenever component re-renders
  }, []); // Empty dependency array to run this effect only once after the component mounts

  const formatDateTime = (dateTimeString: any) => {
    const dateTime = new Date(dateTimeString);
    const date = `${dateTime.getFullYear()}-${(dateTime.getMonth() + 1)
      ?.toString()
      ?.padStart(2, "0")}-${dateTime?.getDate()?.toString()?.padStart(2, "0")}`;
    return date;
  };

  // const handleDateFilter = (filterType: string) => {
  //   setDateFilterType(filterType);
  // };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // setSearchQuery(e.target.value);
    const numericValue = e.target.value.replace(/\D/g, ""); // Remove non-digits
    setSearchQuery(numericValue);
  };

  const handleDateFilter = (filterType: string) => {
    setDateFilterType(filterType);
    if (filterType === "single") {
      setFromDate("");
      setToDate("");
      setSelectedDate(getCurrentDate());
    } else {
      setSelectedDate("");
    }
  };

  const filterDataByDate = () => {
    if (rowData?.salesHistoryDetails?.invoice?.length === 0) {
      return [];
    }
    return rowData?.salesHistoryDetails?.invoice?.filter((item: any) => {
      const itemDate = new Date(item?.invoice_date);

      // Filter by date condition
      if (dateFilterType === "single" && selectedDate) {
        if (formatDateTime(item?.invoice_date) !== selectedDate) {
          return false; // Skip items that don't match the selected single date
        }
      } else if (dateFilterType === "range") {
        if (fromDate && toDate) {
          const fromDateObj = new Date(fromDate);
          const toDateObj = new Date(toDate);
          if (!(itemDate >= fromDateObj && itemDate <= toDateObj)) {
            return false; // Skip items outside the range
          }
        } else {
          if (formatDateTime(item?.invoice_date) !== selectedDate) {
            return false; // Default to current date if range is incomplete
          }
        }
      }
      // Filter by selectedId condition
      if (selectedId) {
        if (selectedId === "All") {
          return true; // Return all data
        } else if (selectedId === "admin") {
          return item?.add_by_admin === true; // Match `add_by_admin` for true
        } else {
          return item?.created_by?.id === selectedId; // Match `id` for specific `selectedId`
        }
      }

      // Default to returning all data
      return true;
    });
  };

  const sortedInvoices = [
    ...(rowData?.salesHistoryDetails?.invoice || []),
  ].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const tableRowData = sortedInvoices?.map((item: any) => {
    return {
      "Invoice Number": item?.is_return ? (
        <>
          <Box>{item?.id}</Box>
          <span style={{ color: "#F0483E" }}>Return Invoice</span>
        </>
      ) : (
        item?.id
      ),
      Date: formatDateTime(item?.invoice_date),
      "Total Amount(RS.)": item?.total_Amount
        ? item?.total_Amount?.toFixed(2)
        : "0.00",
      "Discount(%)": item?.discount_percentage
        ? item?.discount_percentage?.toFixed(2)
        : "0.00",
      "Discount Amount": item?.discount_Amount
        ? item?.discount_Amount?.toFixed(2)
        : "0.00",
      "Total Payable": item?.after_discount_amount
        ? item?.after_discount_amount?.toFixed(2)
        : "0.00",
      Paid: item?.paid_amount ? item?.paid_amount?.toFixed(2) : "0.00",
      "Due(s)": item?.due_amount ? item?.due_amount?.toFixed(2) : "0.00",
      "Change Returned": item?.return_amount
        ? item?.return_amount?.toFixed(2)
        : "0.00",
      Action: (
        <IconButton aria-label="view">
          <PdfIcon
            onClick={() =>
              handleOpenInvoiceModal(item?.id, item?.sales, item?.is_return)
            }
          />
        </IconButton>
      ), // Adding the icon here
      Id: item?.id,
      sales: item?.sales,
      return: item?.is_return,
    };
  });

  useEffect(() => {
    // const filteredData = filterDataByDate()?.filter(
    //   (item: any) => !item?.is_return
    // );

    const filteredData = rowData?.salesHistoryDetails?.invoice;

    const total_amount = filteredData?.reduce(
      (acc: any, item: any) => acc + item?.total_Amount,
      0
    );

    const roundedTotalAmount = parseFloat(total_amount?.toFixed(2));
    setTotalAmount(roundedTotalAmount);

    const total_discount = filteredData?.reduce(
      (acc: any, item: any) => acc + item?.discount_Amount,
      0
    );
    const roundedTotalDiscount = parseFloat(total_discount?.toFixed(2));
    setTotalDiscount(roundedTotalDiscount);

    // Calculate profit
    let totalSellPrice = 0;
    let totalCostPrice = 0;
    let totalDiscountAmount = 0;

    filteredData?.forEach((invoice: any) => {
      // Adding up the discount amount for each invoice
      totalDiscountAmount += Number(invoice?.discount_Amount) || 0;

      invoice?.sales?.forEach((sale: any) => {
        const quantitySold = Number(sale?.quantity_sold) || 0;
        const sellPrice = Number(sale?.sell_price) || 0;
        const costPrice = Number(sale?.cost_price_per_med) || 0;

        totalSellPrice += quantitySold * sellPrice;
        totalCostPrice += quantitySold * costPrice;
      });
    });

    const grossProfit = totalSellPrice - totalCostPrice;
    const netProfit = grossProfit - totalDiscountAmount;

    const roundedTotalRevenue = parseFloat(netProfit?.toFixed(2));

    const roundedTotalCost = parseFloat(totalCostPrice?.toFixed(2));

    setTotalProfit(roundedTotalRevenue);
    setTotalCost(roundedTotalCost);
  }, [selectedDate, fromDate, toDate, rowData, dateFilterType, selectedId]);

  const handleRowClick = (rowData: any) => {
    setSelectedRow(rowData);
    setIsAppModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsAppModalOpen(false);
  };
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const handleSaveAndPrintInvoice = () => {
    handleCloseInvoiceModal(); // Close the modal
  };

  const print = true;

  const [openPdf, setOpenPdf] = useState(false);

  const handleOpenPdf = () => {
    setOpenPdf(true);
  };
  const handleClosePdf = () => {
    setOpenPdf(false);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing page size
  };

  let isPagination = true;

  return (
    <>
      {/* <Box sx={{ p: 3, fontSize: "24px", fontWeight: "bold" }}>
        Sales History
      </Box> */}

      {/* {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "75vh", // Set height to full viewport height
          }}
        >
          <LoadingSpinner />
        </Box>
      ) : ( */}
      <Box
        sx={{
          margin: 1,
          borderRadius: "5px",
          backgroundColor: ColorPallet?.bgcolor,
          minHeight: {
            xs: "auto",
            sm: "80vh",
            lg: "80vh",
          },
          my: "1.2%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            ml: 1,
          }}
        >
          <Box sx={{ p: 1 }}>
            <RadioGroup
              row
              aria-label="date-filter-type"
              name="date-filter-type"
              value={dateFilterType}
              onChange={(e) => handleDateFilter(e?.target?.value)}
            >
              <FormControlLabel
                value="single"
                control={<Radio />}
                label="Select Date"
              />
              <FormControlLabel
                value="range"
                control={<Radio />}
                label="Select Date Range"
              />
            </RadioGroup>
          </Box>
          <Box
            sx={{
              display: "flex",
              px: 3,
              cursor: tableRowData?.length === 0 ? "not-allowed" : "pointer", // Disable pointer interaction
              alignItems: "center",
              justifyContent: "center",
              opacity: tableRowData?.length === 0 ? 0.5 : 1, // Dim the button when disabled
            }}
            onClick={tableRowData?.length === 0 ? undefined : handleOpenPdf} // Disable click when no data
          >
            <Box sx={{ mr: 1 }}>Print Sales History</Box>
            <PdfDownloadIcon />
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "start", sm: "center" },
            gap: 2,
            ml: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            {dateFilterType === "single" && (
              <Box sx={{ p: 0, pl: 0 }}>
                <TextField
                  type="date"
                  size="small"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e?.target?.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ width: "227px" }} // Adjust the width here as needed
                />
              </Box>
            )}

            {dateFilterType === "range" && (
              <Box
                sx={{
                  p: 0,
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                }}
              >
                <TextField
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={(e) => setFromDate(e?.target?.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ height: "36px", width: "200px" }}
                />
                <TextField
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={(e) => setToDate(e?.target?.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ height: "36px", width: "200px" }}
                />
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              gap: 2,
              mr: 2,
              p: 0,
            }}
          >
            {Account_Type === 0 && (
              <Box sx={{ width: { xs: 150, sm: 200 } }}>
                <FormControl fullWidth>
                  <Select
                    value={selectedId}
                    onChange={handleChange}
                    displayEmpty
                    size="small"
                    defaultValue=""
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                          overflowY: "auto",
                        },
                      },
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={"admin"}>Admin</MenuItem>
                    {options.map((option: any) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            {
              <TextField
                // inputRef={searchInputRef} // Assign ref to maintain focus
                variant="outlined"
                size="small"
                placeholder="Search by Invoice ID..."
                value={searchQuery}
                onChange={handleSearchChange}
                inputProps={{ inputMode: "numeric" }} // Shows number keypad on mobile
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: "250px" }}
              />
            }
          </Box>
        </Box>
        <Box sx={{ mt: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: isSmallScreen ? "column" : "row",
                bgcolor: "white",
                gap: 1,
                color: "white",
                p: isSmallScreen ? 1 : 2,
              }}
            >
              {/* Gross Sales */}
              <Box
                sx={{
                  flex: 1,
                  background:
                    "linear-gradient(180deg, #59C8FF 0%, #0375ad 100%)",
                  p: 2,
                  borderRadius: "5px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{ fontSize: "14px", fontWeight: 600, lineHeight: 1.2 }}
                >
                  Gross Sales
                </Box>
                <Box
                  sx={{ fontSize: "14px", fontWeight: 600, lineHeight: 1.2 }}
                >
                  Rs. {totalAmount}
                </Box>
              </Box>

              {/* Total Discount */}
              <Box
                sx={{
                  flex: 1,
                  background:
                    "linear-gradient(180deg, #00A1F1 0%, #133984 100%)",
                  p: 1,
                  borderRadius: "5px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{ fontSize: "14px", fontWeight: 600, lineHeight: 1.2 }}
                >
                  Total Discount
                </Box>
                <Box
                  sx={{ fontSize: "14px", fontWeight: 600, lineHeight: 1.2 }}
                >
                  Rs. {totalDiscount}
                </Box>
              </Box>

              {/* Net Sales */}
              <Box
                sx={{
                  flex: 1,
                  background:
                    "linear-gradient(180deg, #27396E 0%, #0375ad 100%)",
                  p: 1,
                  borderRadius: "5px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{ fontSize: "14px", fontWeight: 600, lineHeight: 1.2 }}
                >
                  Net Sales
                </Box>
                <Box
                  sx={{ fontSize: "14px", fontWeight: 600, lineHeight: 1.2 }}
                >
                  Rs. {totalAmount - totalDiscount}
                </Box>
              </Box>

              {/* Total Cost */}
              <Box
                sx={{
                  flex: 1,
                  background:
                    "linear-gradient(180deg, #8bc9bb 0%, #01918d 100%)",
                  p: 1,
                  borderRadius: "5px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{ fontSize: "14px", fontWeight: 600, lineHeight: 1.2 }}
                >
                  Total Cost
                </Box>
                <Box
                  sx={{ fontSize: "14px", fontWeight: 600, lineHeight: 1.2 }}
                >
                  Rs. {totalCost}
                </Box>
              </Box>

              {/* Net Revenue */}
              <Box
                sx={{
                  flex: 1,
                  background:
                    "linear-gradient(180deg, #1CD9A9 0%, #038a68 100%)",
                  p: 1,
                  borderRadius: "5px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{ fontSize: "14px", fontWeight: 600, lineHeight: 1.2 }}
                >
                  Net Revenue
                </Box>
                <Box
                  sx={{ fontSize: "14px", fontWeight: 600, lineHeight: 1.2 }}
                >
                  Rs. {totalProfit}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "45vh",
            }}
          >
            <LoadingSpinner />
          </Box>
        ) : tableRowData && tableRowData.length > 0 ? (
          <Box sx={{ mt: 1, p: 2, width: "100%" }}>
            <SalesHistoryTable
              headerData={headerData}
              rowData={tableRowData}
              onRowClick={handleRowClick}
              onActionClick={handleOpenInvoiceModal}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        ) : (
          <Typography
            variant="body1"
            sx={{
              mt: 6,
              ml: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              // color: "red",
              fontSize: "14px",
            }}
          >
            {dateFilterType === "range" && (!fromDate || !toDate)
              ? "Please select date first."
              : "No data found."}
          </Typography>
        )}

        {/* {tableRowData && tableRowData?.length > 0 ? (
          <Box sx={{ mt: 5, p: 1, width: "100%" }}>

            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "45vh", // Set height to full viewport height
                }}
              >
                <LoadingSpinner />
              </Box>
            ) : (


              <SalesHistoryTable
                headerData={headerData}
                rowData={tableRowData}
                onRowClick={handleRowClick} // Add row click handler
                onActionClick={handleOpenInvoiceModal}
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Box>
        ) : (
          <Typography
            variant="body1"
            sx={{
              mt: 6,
              ml: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "red",
              fontSize: "14px",
            }}
          >
            {dateFilterType === "range" && (!fromDate || !toDate)
              ? "Please select date first."
              : "No data found for the selected date."}
          </Typography>
        )} */}
        {/* App modal for displaying invoice details */}
        <AppModal
          title="Sales History Details"
          open={isAppModalOpen}
          handleClose={handleCloseModal}
          fullScreen={false}
          maxWidth="lg"
        >
          {isAppModalOpen && selectedRow && (
            <SalesHistoryModal
              invoiceId={selectedRow["Id"]}
              isPagination={isPagination}
            />
          )}

          <Box sx={{ py: 6 }}>
            <AppButton
              variant="contained"
              type="submit"
              title="Close"
              sx={{
                float: "right",
                backgroundColor: "#F9F9F9",
                color: "#000000",
                fontSize: "16px",
                fontWeight: "400",
                "&:hover": {
                  backgroundColor: "#DDDDDD",
                  color: "#000000",
                },
              }}
              onClick={handleCloseModal}
            />
          </Box>
        </AppModal>
      </Box>
      {/* )} */}
      <AppModal
        title="Invoice Details"
        open={openInvoiceModal}
        handleClose={handleCloseInvoiceModal}
        fullScreen={false}
        maxWidth="sm"
        //maxWidth="md"
        // maxWidth={"10%"}
      >
        {isReturnModal ? (
          <ReturnInvoiceModal
            onClose={handleCloseInvoiceModal}
            invoiceId={invoiceId}
            //purchasedItem={purchasedItem}
            returnItem={sale}
            isPrint={print}
            handleSubmit={handleSaveAndPrintInvoice}
            // discountedAmount={discountedAmount}
          />
        ) : (
          <InvoiceModal
            invoiceId={invoiceId}
            onClose={handleCloseInvoiceModal}
            onSaveAndClose={handleSaveAndPrintInvoice}
            isPrint={print}
          />
        )}
      </AppModal>

      <AppModal
        title="Sales History"
        open={openPdf}
        handleClose={handleClosePdf}
        fullScreen={false}
        maxWidth="md"
      >
        <SalesHistoryPdf
          tableRowData={tableRowData}
          handleClosePdf={handleClosePdf}
          totalAmount={totalAmount}
          totalCost={totalCost}
          totalDiscount={totalDiscount}
          totalProfit={totalProfit}
          fromDate={fromDate}
          toDate={toDate}
          selectedDate={selectedDate}
        />
      </AppModal>
    </>
  );
};
export default SalesHistory;
